/**
 * Utility functions for handling images
 */

/**
 * Get the primary image from a product
 * Prioritizes the image field, falls back to first image in images array
 */
export const getPrimaryImage = (product: { images?: string[]; image?: string }): string | null => {
  // Primary image is stored in the 'image' field
  if (product.image) {
    return product.image;
  }
  // Fallback to first image in images array if no primary image
  if (product.images && product.images.length > 0) {
    return product.images[0];
  }
  return null;
};

/**
 * Get all available images from a product
 * Combines single image and images array
 */
export const getAllImages = (product: { images?: string[]; image?: string }): string[] => {
  const allImages: string[] = [];
  
  // Add primary image first
  if (product.image) {
    allImages.push(product.image);
  }
  
  // Add additional images
  if (product.images && product.images.length > 0) {
    allImages.push(...product.images);
  }
  
  return allImages;
};

/**
 * Handle image loading errors
 */
export const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
  const target = event.target as HTMLImageElement;
  target.style.display = 'none';
  target.nextElementSibling?.classList.remove('hidden');
};

/**
 * Check if an image URL is valid
 */
export const isValidImageUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get optimized image URL (placeholder for future image optimization)
 */
export const getOptimizedImageUrl = (url: string, width?: number, height?: number): string => {
  // For now, return the original URL
  // In the future, this could integrate with an image optimization service
  return url;
};

/**
 * Remove an image from the images array by index
 */
export const removeImageAtIndex = (images: string[], index: number): string[] => {
  return images.filter((_, i) => i !== index);
};

/**
 * Move an image from one position to another in the images array
 */
export const moveImage = (images: string[], fromIndex: number, toIndex: number): string[] => {
  const newImages = [...images];
  const [movedImage] = newImages.splice(fromIndex, 1);
  newImages.splice(toIndex, 0, movedImage);
  return newImages;
};

/**
 * Set a specific image as the primary image
 */
export const setPrimaryImage = (
  currentImage: string,
  currentImages: string[],
  newPrimaryIndex: number
): { image: string; images: string[] } => {
  const allImages = [currentImage, ...currentImages];
  const newPrimaryImage = allImages[newPrimaryIndex];
  const remainingImages = allImages.filter((_, index) => index !== newPrimaryIndex);
  
  return {
    image: newPrimaryImage,
    images: remainingImages
  };
};
